import Image from "next/legacy/image";
import clsx from "clsx";

import { Card, Skeleton } from "@chef/components";
import { CurrencyWrapper as Currency } from "@chef/smart-components";
import { BASE_64_1X1_GREY, BRAND_NAME } from "@chef/constants";
import { CalendarQuery } from "@chef/state-management";
import { useLoyalty } from "@chef/feature-flags";
import {
  getWeek,
  getYear,
  isMealboxBasketType,
  nextDeliveryWeekDateObj,
} from "@chef/helpers";
import { sumByFn } from "@chef/utils/array";

import { useMySubscriptionMealbox } from "../../hooks/useMySubscriptionMealbox";
import { isMyAccountSubscriptionProduct } from "../../utils";
import { useMyAccountCalendarQuery } from "../../hooks/useMyAccountCalendarQuery";

import { brand, intl } from "./SubscriptionOverview.Intl";

export const SubscriptionOverview = () => {
  const nextDeliveryWeek = nextDeliveryWeekDateObj();
  const week = getWeek(nextDeliveryWeek);
  const year = getYear(nextDeliveryWeek);

  const { data: calendarQuery, isLoading: isLoadingCalendar } =
    useMyAccountCalendarQuery();

  const { data: loyaltyData } = useLoyalty();

  const {
    meals,
    portions,
    mealboxPrice,
    defaultBasket,
    isLoading: isLoadingBilling,
  } = useMySubscriptionMealbox();

  const addons =
    defaultBasket?.basketProducts.filter((basketProduct) =>
      isMyAccountSubscriptionProduct(basketProduct.variation.product),
    ) || [];

  const addonCount = addons.length;
  const addonSum = sumByFn(
    addons,
    (addon) => addon.variation.finalPrice * addon.quantity,
  );
  const deliveryFee = getDeliveryFeeForWeek({
    calendar: calendarQuery?.calendar,
    week,
    year,
    loyaltyLevel: loyaltyData?.currentLevel,
  });

  const finalSum = mealboxPrice + addonSum + deliveryFee;
  const isLoading = isLoadingBilling || isLoadingCalendar;

  return (
    <Card
      noPadding
      className="flex flex-col gap-2"
      id="abonnement-overview-card"
    >
      <div
        className={clsx(
          BRAND_NAME === "RN" && " bg-lightBlue",
          "relative w-full h-36",
        )}
      >
        <Image
          src={brand.IMAGE_URL || BASE_64_1X1_GREY}
          layout="fill"
          objectFit="cover"
          alt={intl.RECEIPT}
        />
      </div>

      <div className="px-4 pt-4">
        <p>
          <strong>{intl.RECEIPT}</strong>
        </p>
      </div>

      {isLoading && <SkeletonLoader />}

      {!isLoading && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <ReceiptRow
              label={intl.FORMAT_PORTIONS_MEALS({ portions, meals })}
              price={mealboxPrice}
            />
            {addonCount > 0 && (
              <ReceiptRow
                label={`${addonCount} ${intl.FORMAT_ADDONS(addonCount)}`}
                price={addonSum}
              />
            )}
            <ReceiptRow label={intl.FREIGHT_FEE} price={deliveryFee} />
          </div>

          <div className="flex justify-between px-4 pb-4">
            <p>
              <strong>{intl.TOTAL}</strong>
            </p>

            <strong>
              <Currency>{finalSum}</Currency>
            </strong>
          </div>
        </div>
      )}
    </Card>
  );
};

const ReceiptRow = ({ label, price }: { label: string; price: number }) => (
  <div className="flex justify-between px-4">
    <p>{label}</p>

    <Currency>{price}</Currency>
  </div>
);

const getDeliveryFeeForWeek = ({
  calendar,
  week,
  year,
  loyaltyLevel,
}: {
  calendar?: CalendarQuery["calendar"];
  week: number;
  year: number;
  loyaltyLevel?: number;
}) => {
  if (loyaltyLevel === 4 || !calendar) {
    return 0;
  }

  const currentCalendarWeek = calendar.find(
    (w) => w.week === week && w.year === year,
  );

  return (
    currentCalendarWeek?.baskets.find(isMealboxBasketType)?.shippingDetails
      .plannedDeliveryInformation.customerFee || 0
  );
};

const SkeletonLoader = () => (
  <div className="flex flex-col gap-2 p-4">
    <div className="flex justify-between">
      <Skeleton width={32} />
      <Skeleton width={14} />
    </div>

    <div className="flex justify-between">
      <Skeleton width={24} />
      <Skeleton width={10} />
    </div>

    <div className="flex justify-between">
      <Skeleton width={32} />
      <Skeleton width={14} />
    </div>

    <div className="flex justify-between">
      <Skeleton width={24} />
      <Skeleton width={10} />
    </div>
  </div>
);
